import { SymptomEntry, StoredAnalysis, AIAnalysisResult } from '../types';
import { STORAGE_KEY } from '../utils/constants';
import { db, PreferencesRow } from './db';

const DEFAULT_PREFERENCES: PreferencesRow = {
  id: 'singleton',
  demoMode: false,
  notificationsEnabled: false
};

let migration: Promise<void> | null = null;

// One-time migration from the old localStorage blob into IndexedDB.
// The old key is left in place afterward as a safety fallback (not deleted).
function ensureMigrated(): Promise<void> {
  if (!migration) {
    migration = (async () => {
      const [entryCount, analysisCount] = await Promise.all([
        db.entries.count(),
        db.analyses.count()
      ]);
      if (entryCount > 0 || analysisCount > 0) return;

      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;

      try {
        const parsed = JSON.parse(raw);
        const entries: SymptomEntry[] = Array.isArray(parsed.entries) ? parsed.entries : [];
        const analyses: StoredAnalysis[] = Array.isArray(parsed.analyses) ? parsed.analyses : [];

        await db.transaction('rw', db.entries, db.analyses, db.preferences, async () => {
          if (entries.length > 0) await db.entries.bulkPut(entries);
          if (analyses.length > 0) await db.analyses.bulkPut(analyses);
          if (parsed.preferences) {
            await db.preferences.put({ ...DEFAULT_PREFERENCES, ...parsed.preferences, id: 'singleton' });
          }
        });
      } catch (error) {
        console.error('Error migrating legacy localStorage data:', error);
      }
    })();
  }
  return migration;
}

export class StorageService {
  // Entry operations
  static async getAllEntries(): Promise<SymptomEntry[]> {
    await ensureMigrated();
    const entries = await db.entries.toArray();
    return entries.sort((a, b) => a.date.localeCompare(b.date));
  }

  static async getEntryByDate(date: string): Promise<SymptomEntry | null> {
    await ensureMigrated();
    const entry = await db.entries.get(date);
    return entry ?? null;
  }

  static async addEntry(entry: SymptomEntry): Promise<void> {
    await ensureMigrated();
    await db.entries.put(entry);
  }

  static async updateEntry(date: string, updatedEntry: SymptomEntry): Promise<void> {
    await ensureMigrated();
    const existing = await db.entries.get(date);
    if (existing) {
      await db.entries.put(updatedEntry);
    }
  }

  static async deleteEntry(date: string): Promise<void> {
    await ensureMigrated();
    await db.entries.delete(date);
  }

  // Analysis operations
  static async saveAnalysis(analysis: AIAnalysisResult, dataSnapshot: SymptomEntry[]): Promise<void> {
    await ensureMigrated();

    const storedAnalysis: StoredAnalysis = {
      date: new Date().toISOString(),
      analysis,
      dataSnapshot
    };

    await db.analyses.add(storedAnalysis as StoredAnalysis & { id?: number });

    // Keep only the last 10 analyses
    const count = await db.analyses.count();
    if (count > 10) {
      const oldest = await db.analyses.orderBy('id').limit(count - 10).toArray();
      await db.analyses.bulkDelete(oldest.map(a => (a as StoredAnalysis & { id: number }).id));
    }
  }

  static async getLatestAnalysis(): Promise<StoredAnalysis | null> {
    await ensureMigrated();
    const latest = await db.analyses.orderBy('id').last();
    return latest ?? null;
  }

  static async getAllAnalyses(): Promise<StoredAnalysis[]> {
    await ensureMigrated();
    return db.analyses.orderBy('id').toArray();
  }

  // Preferences
  static async getPreferences(): Promise<Omit<PreferencesRow, 'id'>> {
    await ensureMigrated();
    const prefs = await db.preferences.get('singleton');
    return prefs ?? DEFAULT_PREFERENCES;
  }

  static async setDemoMode(enabled: boolean): Promise<void> {
    await ensureMigrated();
    const prefs = (await db.preferences.get('singleton')) ?? DEFAULT_PREFERENCES;
    await db.preferences.put({ ...prefs, demoMode: enabled, id: 'singleton' });
  }

  static async isDemoMode(): Promise<boolean> {
    const prefs = await this.getPreferences();
    return prefs.demoMode;
  }

  // Data management
  static async exportData(): Promise<string> {
    await ensureMigrated();
    const [entries, analyses, preferences] = await Promise.all([
      db.entries.toArray(),
      db.analyses.toArray(),
      db.preferences.get('singleton')
    ]);
    return JSON.stringify(
      { version: '2.0.0', entries, analyses, preferences: preferences ?? DEFAULT_PREFERENCES },
      null,
      2
    );
  }

  static async importData(jsonData: string): Promise<void> {
    await ensureMigrated();
    try {
      const data = JSON.parse(jsonData);

      if (!data.entries || !Array.isArray(data.entries)) {
        throw new Error('Invalid data format');
      }

      await db.transaction('rw', db.entries, db.analyses, db.preferences, async () => {
        await db.entries.clear();
        await db.entries.bulkPut(data.entries);

        if (Array.isArray(data.analyses) && data.analyses.length > 0) {
          await db.analyses.clear();
          await db.analyses.bulkPut(data.analyses);
        }

        if (data.preferences) {
          await db.preferences.put({ ...DEFAULT_PREFERENCES, ...data.preferences, id: 'singleton' });
        }
      });
    } catch (error) {
      console.error('Error importing data:', error);
      throw new Error('Invalid data format. Please check your JSON file.');
    }
  }

  static async clearAllData(): Promise<void> {
    await ensureMigrated();
    await db.transaction('rw', db.entries, db.analyses, db.preferences, async () => {
      await db.entries.clear();
      await db.analyses.clear();
      await db.preferences.put(DEFAULT_PREFERENCES);
    });
  }

  static async clearEntries(): Promise<void> {
    await ensureMigrated();
    await db.entries.clear();
  }
}
